import React from 'react';
import { toast } from 'react-toastify';
import { ENERGY_LABEL_RANGE, ENERGY_LABEL_RATING } from 'b2b-components/theme/components/EnergyLabel';
import { RemoteRecord } from 'b2b-apps/shop/common/redux/RemoteRecord';
import { castToConstant } from 'b2b-apps/shop/common/utils/api/api';
import { ensureArray } from 'b2b-apps/shop/common/utils/array/array';
import { castToBoolean } from 'b2b-apps/shop/common/utils/boolean/boolean';
import { getDate } from 'b2b-apps/shop/common/utils/date/date';
import { ProductToast } from 'b2b-apps/shop/product/components/ProductToast';
import { ProductTileType } from 'b2b-apps/shop/product/redux/products/tile/types';
import { ProductQuantityOrderedType } from 'b2b-apps/shop/product/redux/types';
import { ProductResponseSettingsType, ProductSettingsType } from 'b2b-common/core/product/api/Product.types';

enum PRODUCT_MESSAGES {
  pdfExportError = 'pdfExportError',
  wishlistsSearchError = 'wishlistsSearchError',
}

const showProductToast = (messageId: PRODUCT_MESSAGES): void => {
  toast(<ProductToast messageId={messageId} />, {
    className: 'cds-Toast-Wrapper',
    progressClassName: 'cds-Toast-ProgressBar',
  });
};

const getProductEnergyLabel = (
  energyLabel: {
    range: string;
    value: string;
  } | null,
): {
  range: ENERGY_LABEL_RANGE;
  value: ENERGY_LABEL_RATING;
} | null => {
  let range;
  let value;

  if (energyLabel) {
    range = castToConstant(energyLabel.range, ENERGY_LABEL_RANGE) as ENERGY_LABEL_RANGE;
    value = castToConstant(energyLabel.value, ENERGY_LABEL_RATING) as ENERGY_LABEL_RATING;
  }

  if (!energyLabel || !value || !range) {
    return null;
  }

  return {
    range,
    value,
  };
};

const mapResponseProductQuantityOrderedToState = (
  quantityOrdered: ProductQuantityOrderedType,
): ProductQuantityOrderedType => ensureArray(quantityOrdered).map((order) => ({
  deliveryDate: order.deliveryDate ? getDate(order.deliveryDate) : null,
  quantityOrdered: parseInt(order.quantityOrdered, 10),
}));

const mapResponseProductSettingsToState = (
  settings: Partial<ProductResponseSettingsType>,
): ProductSettingsType => ({
  allowOverstockBuy: castToBoolean(settings.allowOverstockBuy),
  isBuyable: castToBoolean(settings.isBuyable),
  isNew: castToBoolean(settings.isNew),
  isOccasion: castToBoolean(settings.isRefurbished),
  isSellout:
      castToBoolean(settings.isSellout) && !castToBoolean(settings.isRefurbished),
  isSpecialOffer: castToBoolean(settings.isSpecialOffer),
  isSpecialOrder: castToBoolean(settings.isSpecialOrder),
  maximumOrderQuantity: parseInt(`${settings.maximumOrderQuantity}`, 10),
  quantity: parseInt(`${settings.quantity}`, 10),
  quantityOrdered: mapResponseProductQuantityOrderedToState(
    settings.quantityOrdered,
  ),
  requiresLicense: castToBoolean(settings.requiresLicense),
  shouldShowPrice: castToBoolean(settings.showPrice),
  isCoreAssortment: castToBoolean(settings.isCoreAssortment),
  isOwnAssortment: castToBoolean(settings.isOwnAssortment),
});

const isProductGroupLoaded = (
  skus: string[],
  productTilesData: Record<string, RemoteRecord<ProductTileType>>,
): boolean => skus.filter((sku) => !productTilesData[sku]?.wasStarted).length === 0;

const PRODUCT_COUNT_IN_GROUP = 24;

export {
  getProductEnergyLabel,
  isProductGroupLoaded,
  mapResponseProductQuantityOrderedToState,
  mapResponseProductSettingsToState,
  PRODUCT_COUNT_IN_GROUP,
  PRODUCT_MESSAGES,
  showProductToast,
};
